import { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { bindActionCreators } from "redux";
import {
  Client,
  ConnectionState,
  Conversation,
  Message,
  Participant,
} from "@twilio/conversations";

import {
  loadUnreadMessagesCount,
  handleParticipantsUpdate,
  upsertMessage,
} from "../twilio/twilioHelpers";
import { actionCreators, AppState } from "../../store";
import { handlePromiseRejection } from "../../helpers";
import {
  postApiToken,
  setTemplateSendPending,
} from "../../store/action-creators";
import { NOTIFICATION_TIMEOUT } from "../../constants";

type UseTwilioClientResult = {
  client?: Client;
  connectionState?: ConnectionState;
  usernameForHeader: string;
};

export const useTwilioClient = (): UseTwilioClientResult => {
  const [connectionState, setConnectionState] = useState<ConnectionState>();
  const [client, setClient] = useState<Client>();

  const dispatch = useDispatch();
  const {
    upsertMessages,
    updateLoadingState,
    updateParticipants,
    updateUser,
    updateUnreadMessages,
    startTyping,
    endTyping,
    upsertConversation,
    removeMessages,
    removeConversation,
    updateCurrentConversation,
    addNotifications,
    clearAttachments,
    getConversations,
  } = bindActionCreators(actionCreators, dispatch);

  const sid = useSelector((s: AppState) => s.sid);
  const twilioAuth = useSelector((s: AppState) => s.twilio);
  const twilioToken = twilioAuth.token;
  const twilioIdentity = twilioAuth.identity;

  const sidRef = useRef("");
  sidRef.current = sid;

  const usernameForHeader =
    twilioIdentity || localStorage.getItem("username") || "";

  const updateTypingIndicator = (
    participant: Participant,
    sid: string,
    callback: (sid: string, user: string) => void
  ) => {
    const {
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      attributes: { friendlyName },
      identity,
    } = participant;

    if (identity === twilioIdentity) {
      return;
    }
    callback(sid, identity || friendlyName || "");
  };

  useEffect(() => {
    if (!twilioToken) return;

    let reconnectTimer: ReturnType<typeof setTimeout> | null = null;
    let reconnectAttempts = 0;

    const getReconnectDelay = (attempt: number): number => {
      if (attempt < 3) return 1000;
      if (attempt < 6) return 3000;
      return 5000;
    };

    const attemptReconnect = async (currentClient: Client) => {
      reconnectAttempts++;
      const delay = getReconnectDelay(reconnectAttempts);
      console.log(
        `[Twilio] Reconnect attempt ${reconnectAttempts} in ${delay}ms`
      );

      reconnectTimer = setTimeout(async () => {
        try {
          const { token: newToken } = await (dispatch as any)(postApiToken());
          await currentClient.updateToken(newToken);
          console.log("[Twilio] Token refreshed during reconnect");
        } catch (e) {
          console.error("[Twilio] Reconnect failed:", e);
          // Retry again if still disconnected
          if (reconnectAttempts < 10) {
            attemptReconnect(currentClient);
          } else {
            addNotifications([
              {
                id: Date.now(),
                message:
                  "Connessione Twilio interrotta. Ricarica la pagina per riprovare.",
                variant: "error",
                dismissAfter: NOTIFICATION_TIMEOUT,
              },
            ]);
          }
        }
      }, delay);
    };

    const twilioClient = new Client(twilioToken);
    setClient(twilioClient);

    // conversationJoined — lazy loading: only register typing handlers and
    // load lightweight unread counts. Messages and participants are loaded
    // on-demand when the user actually opens the conversation.
    twilioClient.on("conversationJoined", (conversation: Conversation) => {
      upsertConversation(conversation);

      conversation.on("typingStarted", (participant: Participant) => {
        handlePromiseRejection(
          () =>
            updateTypingIndicator(participant, conversation.sid, startTyping),
          addNotifications
        );
      });

      conversation.on("typingEnded", async (participant: Participant) => {
        await handlePromiseRejection(
          async () =>
            updateTypingIndicator(participant, conversation.sid, endTyping),
          addNotifications
        );
      });

      handlePromiseRejection(async () => {
        if (conversation.status === "joined") {
          await loadUnreadMessagesCount(conversation, updateUnreadMessages);
        }
      }, addNotifications);
    });

    // conversationRemoved
    twilioClient.on(
      "conversationRemoved",
      async (conversation: Conversation) => {
        updateCurrentConversation("");
        await handlePromiseRejection(async () => {
          removeConversation(conversation.sid);
          updateParticipants([], conversation.sid);
        }, addNotifications);
      }
    );

    // messageAdded
    twilioClient.on("messageAdded", async (message: Message) => {
      await upsertMessage(
        message,
        sidRef,
        upsertMessages,
        updateUnreadMessages,
        addNotifications
      );
      if (message.author === twilioIdentity) {
        clearAttachments(message.conversation.sid, "-1");
        dispatch(setTemplateSendPending(message.conversation.sid, false));
        await getConversations();
      } else {
        // Messaggio del cliente: aggiorno la lista conversazioni per riflettere
        // l'eventuale cambio di stato (es. Pending/Inactive → Active)
        await getConversations();
      }
    });

    // userUpdated
    twilioClient.on("userUpdated", async (event) => {
      await updateUser(event.user);
    });

    // participants
    twilioClient.on("participantLeft", async (participant: Participant) => {
      await handlePromiseRejection(
        async () => handleParticipantsUpdate(participant, updateParticipants),
        addNotifications
      );
    });

    twilioClient.on("participantUpdated", async (event) => {
      await handlePromiseRejection(
        async () =>
          handleParticipantsUpdate(event.participant, updateParticipants),
        addNotifications
      );
    });

    twilioClient.on("participantJoined", async (participant: Participant) => {
      await handlePromiseRejection(
        async () => handleParticipantsUpdate(participant, updateParticipants),
        addNotifications
      );
    });

    // conversationUpdated
    twilioClient.on("conversationUpdated", async ({ conversation }) => {
      await handlePromiseRejection(
        () => upsertConversation(conversation),
        addNotifications
      );
    });

    // messageUpdated
    twilioClient.on("messageUpdated", async ({ message }) => {
      await handlePromiseRejection(
        async () =>
          upsertMessage(
            message,
            sidRef,
            upsertMessages,
            updateUnreadMessages,
            addNotifications
          ),
        addNotifications
      );
    });

    // messageRemoved
    twilioClient.on("messageRemoved", async (message: Message) => {
      await handlePromiseRejection(
        () => removeMessages(message.conversation.sid, [message]),
        addNotifications
      );
    });

    // pushNotification
    twilioClient.on("pushNotification", (event: any) => {
      if (event.type !== "twilio.conversations.new_message") {
        return;
      }

      if (Notification.permission === "granted") {
        // showNotification(event);
      } else {
        console.log("Push notification is skipped", Notification.permission);
      }
    });

    twilioClient.on("tokenAboutToExpire", async () => {
      try {
        console.log("tokenAboutToExpire");
        const { token: newTwilioToken } = await (dispatch as any)(
          postApiToken()
        );
        await twilioClient.updateToken(newTwilioToken);
      } catch (e) {
        console.error("Error in tokenAboutToExpire:", e);
      }
    });

    twilioClient.on("tokenExpired", async () => {
      try {
        console.log("tokenExpired");
        const { token: newTwilioToken } = await (dispatch as any)(
          postApiToken()
        );
        await twilioClient.updateToken(newTwilioToken);
      } catch (e) {
        console.error("Error in tokenExpired:", e);
      }
    });

    twilioClient.on("connectionStateChanged", (state: ConnectionState) => {
      setConnectionState(state);

      if (state === "connected") {
        // Reset retry counter on successful connection
        reconnectAttempts = 0;
        if (reconnectTimer) {
          clearTimeout(reconnectTimer);
          reconnectTimer = null;
        }
      } else if (state === "disconnected" || state === "denied") {
        // Attempt reconnection with backoff
        attemptReconnect(twilioClient);
      }
    });

    // Error handler — suppress noisy Twilsock timeout toasts and let the
    // SDK reconnect on its own. Only surface non-timeout errors.
    twilioClient.on("connectionError", (error: any) => {
      const msg: string = error?.message ?? error?.toString?.() ?? "";
      const isTwilsockTimeout =
        msg.toLowerCase().includes("twilsock") &&
        msg.toLowerCase().includes("request timeout");

      if (isTwilsockTimeout) {
        // Twilsock timeouts are transient — the SDK retries automatically.
        // Logging only for debugging; no toast.
        console.warn("[Twilio] Twilsock request timeout (suppressed)", msg);
        return;
      }

      console.error("[Twilio] connectionError:", error);
      addNotifications([
        {
          id: Date.now(),
          message: msg || "Errore di connessione Twilio.",
          variant: "error",
          dismissAfter: NOTIFICATION_TIMEOUT,
        },
      ]);
    });

    updateLoadingState(false);

    return () => {
      if (reconnectTimer) {
        clearTimeout(reconnectTimer);
      }
      twilioClient.removeAllListeners();
    };
  }, [twilioToken]);

  return { client, connectionState, usernameForHeader };
};
